import { Suspense, useState } from 'react'

import type { Space } from '@tribeplatform/gql-client/types'
import {
  CollectionDeleteModal,
  CollectionFormModal,
  CollectionOrganizationModal,
} from '@tribeplatform/react-components/Collection'
import { T } from '@tribeplatform/react-components/i18n'
import { useRouter } from '@tribeplatform/react-sdk'
import { Accordion } from '@tribeplatform/react-ui-kit/Accordion'
import { SimpleDivider } from '@tribeplatform/react-ui-kit/Divider'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { NavVertical } from '@tribeplatform/react-ui-kit/Sidebar'

import { CollectionItemButton } from './CollectionItemButton.js'
import { SpaceItem } from './SpaceItem.js'

interface Props {
  id: string
  name: string
  description?: string
  spaces: Space[]
  defaultOpen?: boolean
  noAddSpace?: boolean
  showDivider?: boolean
}
export const CollectionItem = ({
  id,
  name,
  description,
  spaces,
  defaultOpen,
  noAddSpace,
  showDivider = true,
}: Props) => {
  const { Link } = useRouter()
  const [openOrganizationModal, setOpenOrganizationModal] = useState(false)
  const [openCollectionModal, setOpenCollectionModal] = useState(false)
  const [openDeleteCollectionModal, setOpenDeleteCollectionModal] =
    useState(false)

  return (
    <>
      <Accordion variant="chevron-down" defaultOpen={defaultOpen}>
        <Accordion.Button>
          {noAddSpace ? (
            <span className="truncate font-semibold">{name}</span>
          ) : (
            <CollectionItemButton
              id={id}
              name={name}
              openOrganizationModal={() => setOpenOrganizationModal(true)}
              openCollectionModal={() => setOpenCollectionModal(true)}
              openDeleteCollectionModal={() =>
                setOpenDeleteCollectionModal(true)
              }
            />
          )}
        </Accordion.Button>
        <Accordion.Panel noPadding>
          <NavVertical.Group>
            {spaces.map(space => (
              <SpaceItem key={space.id} space={space} indentation />
            ))}
            {!noAddSpace && (
              <NavVertical.Item
                as={Link}
                href={
                  id
                    ? `/manage/customizer/create-space?collectionId=${id}`
                    : `/manage/customizer/create-space`
                }
                leadingAddon={
                  <div className="me-3 -ms-1">
                    <SvgIcon name="package-plus" size="lg" />
                  </div>
                }
                className="ps-9"
              >
                <T id="Generics.AddSpace" defaultMessage="Add {SPACE}" />
              </NavVertical.Item>
            )}
          </NavVertical.Group>
        </Accordion.Panel>
      </Accordion>
      {showDivider && <SimpleDivider variant="subdued" />}
      {openDeleteCollectionModal && (
        <Suspense fallback={<></>}>
          <CollectionDeleteModal
            open={openDeleteCollectionModal}
            onClose={() => setOpenDeleteCollectionModal(false)}
            id={id}
            name={name}
            spaces={spaces}
            skipNavigate
          />
        </Suspense>
      )}
      {openCollectionModal && (
        <Suspense fallback={<></>}>
          <CollectionFormModal
            open={openCollectionModal}
            id={id}
            name={name}
            description={description}
            onClose={() => setOpenCollectionModal(false)}
          />
        </Suspense>
      )}
      {openOrganizationModal && (
        <Suspense fallback={<></>}>
          <CollectionOrganizationModal
            open={openOrganizationModal}
            onClose={() => setOpenOrganizationModal(false)}
            id={id}
            spaces={spaces}
          />
        </Suspense>
      )}
    </>
  )
}
